/** Kontakt: photo hero, the enquiry form beside the address and hours, the
 *  consent-gated map, and a closing invitation. */

import { useSearchParams } from 'react-router-dom'
import InquiryForm from '../components/InquiryForm'
import LocationMap from '../components/LocationMap'
import { PhotoHero } from '../components/ui'
import type { ContentBlock, PageContent } from '../lib/api'
import { useApi, useDocumentMeta, useHreflang, useLocale } from '../lib/hooks'
import { pagePaths, t } from '../lib/i18n'

const HERO_BG = '/media/FE527118-FBDE-44B4-BBC3-BD447CACBF58_1_105_c.jpeg'

/** What Google is asked to find. The workshop is a listed business, so the
 *  name pins the marker on it rather than on the building - and the address
 *  is what makes the search unambiguous. Deliberately not read out of the
 *  address block: that text is the owner's to reword freely, and a map that
 *  silently moves to another city when he edits a line would be worse than
 *  one that has to be corrected here. */
const MAPS_QUERY = 'Zvi Dori Geigenbaumeister, Schiffgraben 61, 30175 Hannover'

function renderLine(line: string, index: number) {
  if (line.includes('@') && !line.includes(' ')) {
    return (
      <p key={index} className="m-0 pt-3">
        <a href={`mailto:${line}`} className="text-ink underline decoration-line underline-offset-4 hover:decoration-ink">
          {line}
        </a>
      </p>
    )
  }
  if (/^\+?[\d\s/()-]{8,}$/.test(line)) {
    return (
      <p key={index} className="m-0">
        <a href={`tel:${line.replace(/[^\d+]/g, '')}`} className="hover:underline">
          {line}
        </a>
      </p>
    )
  }
  return (
    <p key={index} className="m-0">
      {line}
    </p>
  )
}

/** `?thema=` values (used in links from the Service page) → topic keys. */
const THEMA_TO_TOPIC: Record<string, string> = {
  reparatur: 'repair',
  repair: 'repair',
  gutachten: 'appraisal',
  appraisal: 'appraisal',
  kauf: 'purchase',
  purchase: 'purchase',
}

export default function Kontakt() {
  const locale = useLocale()
  const strings = t(locale)
  const [searchParams] = useSearchParams()
  const defaultTopic = THEMA_TO_TOPIC[searchParams.get('thema') ?? ''] ?? ''
  const { data } = useApi<PageContent>(`/api/pages/kontakt?locale=${locale}`)

  useDocumentMeta(data?.title || strings.meta.kontakt.title, strings.meta.kontakt.description)
  useHreflang(pagePaths('kontakt'))

  const blocks: ContentBlock[] = data?.blocks ?? []
  const hero = blocks.find((b) => b.type === 'hero')
  const texts = blocks.filter((b) => b.type === 'text')

  // Block order: address block, opening hours, invitation heading, then the
  // invitation paragraphs.
  const addressLines = (texts[0]?.text ?? '').split('\n').filter(Boolean)
  const hoursLines = (texts[1]?.text ?? '').split('\n').filter(Boolean)
  const talkLines = (texts[2]?.text ?? '').split('\n').filter(Boolean)
  const invitationText = texts
    .slice(3)
    .map((block) => block.text ?? '')
    .join('\n')
  const invitationParas = invitationText.split('\n').filter(Boolean)

  // "Kontakt / Zvi Dori / …" - the first line doubles as the column heading.
  const [addressHeading, ...addressRest] = addressLines
  const [hoursHeading, ...hoursRest] = hoursLines
  const [talkHeading, ...talkSub] = talkLines

  return (
    <>
      <PhotoHero
        image={HERO_BG}
        title={hero?.eyebrow || strings.meta.kontakt.heading}
        subtitle={hero?.headline}
      />

      <section className="bg-paper px-6 py-16 md:px-10 md:py-24" data-testid="kontakt-body">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-14 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-20">
          <div>
            <h2 className="m-0 mb-8 font-serif text-[32px] font-medium leading-[1.15] text-ink">
              {strings.form.heading}
            </h2>
            <InquiryForm showTopics defaultTopic={defaultTopic} showPhotos />
          </div>

          <aside className="font-sans text-[17px] leading-[1.6] text-slate">
            {addressHeading ? (
              <h2 className="m-0 mb-5 font-serif text-[32px] font-medium leading-[1.15] text-ink">
                {addressHeading}
              </h2>
            ) : null}
            <div data-testid="contact-details">
              {addressRest.map((line, index) => renderLine(line, index + 1))}
            </div>

            {hoursHeading ? (
              <h3 className="m-0 mb-2 mt-10 font-serif text-[26px] font-medium leading-[1.2] text-ink">
                {hoursHeading}
              </h3>
            ) : null}
            {hoursRest.map((line, index) => (
              <p key={index} className="m-0">
                {line}
              </p>
            ))}
          </aside>
        </div>

        {/* The map closes the section rather than opening one of its own -
            the address it belongs to is directly above it. */}
        <div className="mx-auto mt-16 max-w-[1200px]">
          <LocationMap query={MAPS_QUERY} />
        </div>
      </section>

      {/* Closing invitation on the deeper paper tone. */}
      <section className="bg-paper-deep px-6 py-16 md:px-10 md:py-24" data-testid="lets-talk">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
          <div>
            {talkHeading ? (
              <h2 className="rule m-0 font-serif text-[clamp(32px,4vw,46px)] font-medium leading-[1.1] text-ink">
                {talkHeading}
              </h2>
            ) : null}
            {talkSub.length ? (
              <p className="m-0 mt-6 font-serif text-[22px] italic leading-[1.35] text-slate">{talkSub.join(' ')}</p>
            ) : null}
          </div>
          <div className="font-sans text-[18px] leading-[1.65] text-ink">
            {invitationParas.map((line, index) => (
              <p key={index} className="m-0 mb-[1.1em] last:mb-0">
                {line}
              </p>
            ))}
          </div>
        </div>
      </section>

      <span className="sr-only">{strings.nav.kontakt}</span>
    </>
  )
}
